import React, { Suspense, useEffect, useState } from 'react';
import { useSelector } from 'react-redux'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Flowbite } from "flowbite-react";
import { SignedIn, SignedOut, } from "@clerk/clerk-react";
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { HelmetProvider } from 'react-helmet-async';
import { SideNav } from "./components/SideNav";
import { useJwt } from './helpers/common'
import "react-datepicker/dist/react-datepicker.css";
import { useOrganizationService } from './services';
import { NotificationProvider } from './contexts/NotificationContext';
import NotificationListener from './components/NotificationListener';
import { initializeRepositories } from './services/repositories';
import Inbox from "./pages/Inbox";
import Settings from './pages/Settings';
import Resource from "./pages/Resource";
import ManageResources from "./pages/ManageResources";
import Projects from './pages/Projects';
import DashboardNew from './pages/DashboardNew';
import OnboardingPage from './pages/Onboarding/OnboardingPage';
import InviteTeam from './pages/Onboarding/sections/InviteTeam';
import DashboardDetails from "./pages/DashboardNew/RoadMapDetails";
import RoadMap from "./pages/RoadMap";
import OrgInfo from "./pages/Onboarding/sections/OrgInfo";
import SignUp from "./pages/Home/SignUp";
import ProfileInfo from "./pages/Onboarding/sections/ProfileInfo";
import InviteTeams from "./pages/Onboarding/sections/InviteTeams";
import ChatBotAI from './pages/ChatBotAI';
import ChatClient from './pages/ChatClient';
import Templates from "./pages/Templates";
import NotFoundPage from "./pages/Error/404";
import Home from "./pages/Home";
import About from "./pages/Home/About";
import Pricing from "./pages/Home/Pricing";
import PrivacyPolicy from "./pages/Home/PrivacyPolicy";
import TermsOfService from "./pages/Home/TermsOfService";
import Licensing from "./pages/Home/Licensing";
import Contact from "./pages/Home/Contact";
import JoinWaitList from "./pages/Home/JoinWaitList";
import SlackIntegration from "./pages/Home/SlackIntegration";
import ShareProject from './pages/ShareProject';
import ShareChat from './pages/Chat/ShareChat';
import SideNavProvider from './context/SideNavContext';
import ErrorBoundary from './pages/Error/ErrorBoundary';
import ProjectView from "./pages/ProjectView";
import TourGuideComponent from './components/TourGuide';
import HelpCenter from "./pages/HelpCenter";
import { StrategyMapsPage } from './pages/StrategyMaps/StrategyMaps';
import { StrategyMapsViewPage } from './pages/StrategyMaps/StrategyMapsView';
import { useRouteHistory } from './hooks/useRouteHistory';
import { DefaultRenders } from './components/DefaultRenders';
import { ORG_HIDE_STRATEGY_MAPS } from './constants/constants';
import { ResetOnBoarding } from './pages/ResetOnboarding/ResetOnboaring';

// Redirects to / when org doesn't have Strategy Maps
function StrategyMapsRouteGuard({ children }: { children: React.ReactNode }) {
  const JWTdata: any = useJwt();
  const orgId = JWTdata?.org_id || "";
  if (orgId === ORG_HIDE_STRATEGY_MAPS) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

// Simple Dashboard component
const Dashboard = () => {
  return (
    <ProjectView />
  );
};

function App() {

  const ldClient = useLDClient();
  const JWTdata: any = useJwt()
  const [dark, setDark] = useState(true);
  const { orgWorkingDays = [], orgWorkingDaysLoading } = useSelector((state: any) => state.organization)
  const { org_id = '' } = JWTdata

  const { fetchOrgWorkingDays } = useOrganizationService()

  useEffect(() => {
    if (!ldClient) return;

    // Only identify when we have a valid org_id
    if (org_id && org_id !== '') {
      const ctx = {
        kind: 'organization',
        key: `${org_id}`,
        name: org_id // Add additional context
      };

      try {
        ldClient.identify(ctx);
      } catch (error) {
        console.warn('LaunchDarkly identify failed:', error);
      }
    }
  }, [ldClient, org_id]);

  useEffect(() => {
    if (localStorage && localStorage.getItem("theme")) {
      let dark = localStorage && localStorage.getItem("theme") === "dark"
      setDark(dark);
    } else {
      setDark(true);
    }
  }, [])

  useEffect(() => {
    if (org_id && localStorage) {
      let workingDay: any = localStorage.getItem("workingDays") ? localStorage.getItem("workingDays") : ""
      let workingDays: any = workingDay ? JSON.parse(workingDay) : {};
      if (!workingDays[org_id] && !orgWorkingDays.length && !orgWorkingDaysLoading) {
        fetchOrgWorkingDays(org_id)
      } else if (orgWorkingDays.length) {
        workingDays[org_id] = orgWorkingDays
        localStorage.setItem("workingDays", JSON.stringify(workingDays))
      }
    }
  }, [org_id, orgWorkingDaysLoading])

  // Initialize IndexedDB repositories on app startup
  useEffect(() => {
    initializeRepositories();
  }, [])




  




  const renderSuspense = () => {
    return "";
  }

  return (
    <HelmetProvider>
      <NotificationProvider>
        <div className="max-h-[100%] relative">
          <Suspense fallback={renderSuspense()}>
            <ErrorBoundary>
              <Flowbite theme={{ dark }}>
                <BrowserRouter>
                  <SideNavProvider>
                    <SignedIn>
                      <DefaultRenders />
                      <NotificationListener />
                      <div style={{ display: "flex" }}>
                        <ConditionalRendering />
                        <Routes>
                          <Route path="/" element={<Projects />} />
                          <Route path="/v" element={<Projects />} />
                          <Route path="/notification" element={<Inbox />} />
                          <Route path="/notification/v" element={<Inbox />} />
                          <Route path="/dashboard" element={<Dashboard />} />
                          <Route path="/dashboard/v" element={<Dashboard />} />
                          {/* <Route
                            path="/strategymaps"
                            element={<DashboardNew />}
                          /> */}
                          {/* <Route
                            path="/strategymaps/v"
                            element={<DashboardNew />}
                          /> */}
                          {/* <Route path="/strategymaps/:roadmap_id" element={<DashboardDetails />} /> */}
                          <Route
                            path="/strategymaps/:roadmap_id/v"
                            element={<DashboardDetails />}
                          />
                          {/* Roadmaps routes - accessed from Projects page, highlights Work Units in sidenav */}
                          <Route
                            path="/roadmaps/:roadmap_id"
                            element={<DashboardDetails />}
                          />
                          <Route
                            path="/roadmaps/:roadmap_id/v"
                            element={<DashboardDetails />}
                          />
                          <Route path="/settings/*" element={<Settings />} />
                          <Route
                            path="settings/inviteuser"
                            element={<InviteTeam />}
                          />
                          <Route
                            path="settings/inviteuser/v"
                            element={<InviteTeam />}
                          />
                          <Route path="resources" element={<Resource />} />
                          <Route path="resources/v" element={<Resource />} />
                          <Route path="people" element={<ManageResources />} />
                          <Route path="teams" element={<ManageResources />} />
                          <Route path="workitems" element={<Projects />} />
                          <Route path="workitems/v" element={<Projects />} />
                          <Route
                            path="projects/:project_id"
                            element={<DashboardDetails />}
                          />
                          <Route
                            path="projects/:project_id/v"
                            element={<DashboardDetails />}
                          />
                          <Route path="roadmapold" element={<RoadMap />} />
                          <Route path="roadmapold/v" element={<RoadMap />} />
                          <Route path="/welcome" element={<OnboardingPage />} />
                          <Route path="onboarding/org" element={<OrgInfo />} />
                          <Route path="/signup" element={<SignUp />} />
                          <Route
                            path="onboarding/profileinfo"
                            element={<ProfileInfo />}
                          />
                          <Route
                            path="setting/inviteteam"
                            element={<InviteTeams />}
                          />
                          <Route
                            path="setting/inviteteam/v"
                            element={<InviteTeams />}
                          />
                          <Route
                            path="shortcut/resetonboarding"
                            element={<ResetOnBoarding />}
                          />
                          <Route path="chatbotai" element={<ChatBotAI />} />
                          <Route path="chatbotai/v" element={<ChatBotAI />} />
                          <Route path="chatclient" element={<ChatClient />} />
                          <Route path="chatclient/v" element={<ChatClient />} />
                          <Route
                            path="/projects/:token/sharelink"
                            element={<ShareProject />}
                          />
                          <Route
                            path="/chat/:token/share"
                            element={<ShareChat />}
                          />
                          <Route path="templates" element={<Templates />} />
                          <Route path="templates/v" element={<Templates />} />
                          <Route
                            path="templates/:template_id"
                            element={<DashboardDetails />}
                          />
                          <Route
                            path="templates/:template_id/v"
                            element={<DashboardDetails />}
                          />

                          <Route
                            path="strategymaps/"
                            element={
                              <StrategyMapsRouteGuard>
                                <StrategyMapsPage />
                              </StrategyMapsRouteGuard>
                            }
                          />
                          <Route
                            path="strategymaps/:strategymap_id"
                            element={
                              <StrategyMapsRouteGuard>
                                <StrategyMapsViewPage />
                              </StrategyMapsRouteGuard>
                            }
                          />

                          <Route
                            path="/slack"
                            element={
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  width: "100%",
                                  minHeight: "100vh",
                                }}
                              >
                                <SlackIntegration />
                              </div>
                            }
                          />
                          <Route
                            path="/privacy-policy"
                            element={
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  width: "100%",
                                  minHeight: "100vh",
                                }}
                              >
                                <PrivacyPolicy />
                              </div>
                            }
                          />
                          <Route
                            path="/terms-of-service"
                            element={
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  width: "100%",
                                  minHeight: "100vh",
                                }}
                              >
                                <TermsOfService />
                              </div>
                            }
                          />
                          <Route
                            path="/about"
                            element={
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  width: "100%",
                                  minHeight: "100vh",
                                }}
                              >
                                <About />
                              </div>
                            }
                          />
                          <Route
                            path="/pricing"
                            element={
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  width: "100%",
                                  minHeight: "100vh",
                                }}
                              >
                                <Pricing />
                              </div>
                            }
                          />
                          <Route
                            path="/licensing"
                            element={
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  width: "100%",
                                  minHeight: "100vh",
                                }}
                              >
                                <Licensing />
                              </div>
                            }
                          />
                          <Route
                            path="/help-center"
                            element={
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  width: "100%",
                                  minHeight: "100vh",
                                }}
                              >
                                <HelpCenter />
                              </div>
                            }
                          />
                          <Route
                            path="/contact"
                            element={
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  width: "100%",
                                  minHeight: "100vh",
                                }}
                              >
                                <Contact />
                              </div>
                            }
                          />
                          <Route
                            path="/book-a-demo"
                            element={
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  width: "100%",
                                  minHeight: "100vh",
                                }}
                              >
                                <JoinWaitList />
                              </div>
                            }
                          />

                          <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                      </div>
                    </SignedIn>
                    <SignedOut>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Routes>
                          <Route path="/" element={<Home />} />
                          <Route
                            path="/onboarding"
                            element={<OnboardingPage />}
                          />
                          <Route path="/about" element={<About />} />
                          <Route path="/pricing" element={<Pricing />} />
                          <Route path="/slack" element={<SlackIntegration />} />
                          <Route
                            path="/privacy-policy"
                            element={<PrivacyPolicy />}
                          />
                          <Route
                            path="/terms-of-service"
                            element={<TermsOfService />}
                          />
                          <Route path="/licensing" element={<Licensing />} />
                          <Route path="/help-center" element={<HelpCenter />} />
                          <Route path="/contact" element={<Contact />} />
                          <Route
                            path="/book-a-demo"
                            element={<JoinWaitList />}
                          />
                          <Route
                            path="/projects/:token/sharelink"
                            element={<ShareProject />}
                          />
                          <Route
                            path="/chat/:token/share"
                            element={<ShareChat />}
                          />
                          <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                      </div>
                    </SignedOut>
                  </SideNavProvider>
                </BrowserRouter>
              </Flowbite>
            </ErrorBoundary>
          </Suspense>
        </div>
      </NotificationProvider>
    </HelmetProvider>
  );
}

const ConditionalRendering = () => {
  const loc = useLocation()

  if (!loc.pathname.includes("onboarding")
    && !loc.pathname.includes("signup")
    && !loc.pathname.includes("sharelink")
    && !(loc.pathname.includes("/chat/") && loc.pathname.includes("/share"))
    && !loc.pathname.includes("/slack")
    && !loc.pathname.includes("/privacy-policy")
    && !loc.pathname.includes("/terms-of-service")
    && !loc.pathname.includes("/pricing")
    && !loc.pathname.includes("/about")
    && !loc.pathname.includes("/book-a-demo")
    && !loc.pathname.includes("/licensing")
    && !loc.pathname.includes("/help-center")
    && !loc.pathname.includes("/contact")
  ) {
    return (
      <>
        <TourGuideComponent />
        <SideNav />
      </>
    );
  }

  return null;
};

export default App;